import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useActiveClientContext } from 'store/active-client';
import MainLayout from 'components/main-layout/MainLayout';
import AppLoading from 'components/AppLoading';
import API from './Api';

interface IProps {
  children: React.ReactNode;
}

/**
 *
 * ClientProvider HOC
 *
 * All routes that should represent the client in administration side,
 * should be wrapped by ClientProvier HOC
 *
 * Its purpose is to set the correct API Url that should be used
 * by fetching it from server - passing client ID (tenant name)
 *
 * Loading/Error mechanism ensures that the route is fully loaded
 * only after the API Url is set, or if there isn't any error
 * loading the client info requested.
 *
 */
const ClientProvider: FC<IProps> = ({ children }: IProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { clientId } = useParams<{ clientId: string | undefined }>();

  const { client, setActiveClient } = useActiveClientContext();

  useEffect(() => {
    /**
     * Get client and switch to client API
     * before loading any components BUT
     * only if the client hasn't already been loaded
     */
    if (clientId && client && clientId !== client.id) {
      API.get(`clients/${clientId}`)
        /**
         *
         * Get basic information about client by `id`
         *
         */

        .then(res => {
          setActiveClient!({
            id: res.data?.id,
            name: res.data?.name,
            url:
              process.env.NODE_ENV === 'development'
                ? `http://${res.data?.api_url}:${process.env.REACT_APP_BE_PORT}/api`
                : `https://${res.data?.api_url}/api`,
            type: res.data?.type,
            templates: res.data?.client_features?.templates,
            is_multilingual: res.data?.client_features?.is_multilingual,
            leaves: res.data?.client_features?.leaves,
            is_form_76: res.data?.client_features?.form_76,
            permissions_scope: res.data?.client_features?.permissions_scope,
            is_hourly_absences: res.data?.client_features?.hourly_absences,
            is_health_and_safety_training: res.data?.client_features?.health_and_safety_training,
            is_work_trips: res.data?.client_features?.work_trips,
          });
        })
        .catch(err => {
          setError(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  if (isLoading) return <AppLoading />;

  if (error)
    // or we could redirect to 404 page??
    return <MainLayout title="Грешка">Не същестува такъв клиент</MainLayout>;

  return <>{children}</>;
};

export default ClientProvider;
